import { AppDebug, AppDebugLogType } from "../app/debug/AppDebug";
import { IToggle } from "../interfaces/IToggle";
import { ToggleButton } from "./ToggleButton";


export type ToggleGroupItemSelectedEventData = {
    itemIndex: number
}

export enum ToggleGroupEvents {
    ITEM_SELECTED = "item_selected"
}

/**
 * NOTE: createCallback AND createMultipleCallback DO NOT fire when adding items via constructor;
 *  This doesnt fire: var toggleGroup = new ToggleGroup(scene, [item1, item2]);
 *  This does fire: var toggleGroup = new ToggleGroup(scene); toggleGroup.addMultiple(item1,item2);
 * 
 * Version history:
 * 1.0.1    20220919
 * - removeAll added
 */
export class ToggleGroup
{
    protected items: IToggle[];
    protected selectedItem: IToggle = null;
    protected eventEmitter: Phaser.Events.EventEmitter;


    public GetEventEmitter():Phaser.Events.EventEmitter
    {
        return this.eventEmitter;
    }
    
    public GetLength(): number
    {
        return this.items.length;
    }

    public GetItems(): IToggle[]
    {
        return this.items;
    }

    public GetItemByIndex(itemIndex:number): IToggle | null
    {
        return itemIndex < this.items.length ? this.items[itemIndex] : null;
    }

    public GetLast(): IToggle | null
    {
        return this.items.length > 0 ? this.items[this.items.length-1] : null;
    }

    public GetSelectedItemIndex(): number
    {
        return this.items.indexOf(this.selectedItem);
    }

    public SelectItemByIndex(itemIndex: number): void
    {
        // console.log("toggleGroup::SelectItemByIndex; itemIndex=",itemIndex,", items.length=",this.items.length,",items=",this.items)
        const item: IToggle = this.items[itemIndex];
        this.SelectItem(item);
    }

    public SelectItem(item: IToggle): void
    {
        if (!item.IsSelected()) {
            if (this.selectedItem != null) {
                this.selectedItem.SetSelected(false);
            }
            item.SetSelected(true);
            this.selectedItem = item;
        }
    }

    /**
     * Add toggle item
     * @param item 
     */
    public AddItem(item: IToggle): void
    {
        // console.log("AddItem");

        if (this.items.indexOf(item) == -1) {
            this.items.push(item);

            if (this.selectedItem == null) {
                if (item.IsSelected()) {
                    this.selectedItem = item;
                }
            }

            // listen toggle click event
            (item as unknown as Phaser.GameObjects.GameObject).on(
                // Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.OnItemPointerDown, 
                Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnItemPointerUp, 
                {
                    item: item, 
                    toggleGroup: this,
                    // you could cache this, however you'd have to recompute all indexes
                    // once not a trailing item has been removed (or model shuffled in any way)
                    // current approach: everytime you need item index, use this.items.indexOf(item)
                    // itemIndex: this.items.length - 1, 
                }
            );
        }
    }

    public AddItems(...items: IToggle[]): void
    {
        items.forEach(item=>{
            this.AddItem(item);
        })
    }

    public RemoveLast(): IToggle | null
    {
        if (this.items.length > 0) {
            // also deselects (automated), else we'd just pop() :-)
            return this.RemoveItemByIndex(this.items.length - 1);
        }
        return null;
    }

    public RemoveItem(item: IToggle): IToggle | null
    {
        const itemIndex: number = this.items.indexOf(item);
        if (itemIndex != -1) {
            return this.RemoveItemByIndex(itemIndex);
        }
        return null;
    }

    public RemoveItemByIndex(itemIndex:number): IToggle
    {
        const item:IToggle = this.items.splice(itemIndex, 1)[0];

        if (this.selectedItem == item) {
            this.selectedItem.SetSelected(false);
            this.selectedItem = null;
        }
        (item as unknown as Phaser.GameObjects.GameObject).off(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.OnItemPointerUp);

        return item;
    }

    public RemoveAll(): void
    {
        this.items.forEach((item:IToggle)=>{
            if (this.selectedItem == item) {
                this.selectedItem.SetSelected(false);
                this.selectedItem = null;
            }
            (item as unknown as Phaser.GameObjects.GameObject).off(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnItemPointerUp);
        });
    }

    constructor()
    {
        this.items = [];
        this.eventEmitter = new Phaser.Events.EventEmitter();
    }

    protected OnItemPointerUp(): void
    {
        // console.log("OnItemPointerDown");

        const toggleGroup: ToggleGroup = this["toggleGroup"] as ToggleGroup;
        const item: ToggleButton = this["item"] as ToggleButton;

        // NEW 20221004
        if (!item.IsSelected()) {
            // const itemIndex: number = this["itemIndex"] as number; // not caching, becaus not shuffle handling (see above)
            const itemIndex: number = toggleGroup.items.indexOf(item);
            // console.log(item,toggleGroup)
            // console.log("ToggleGroup::OnItemPointerDown", item.index, item.toggleGroup);

            toggleGroup.SelectItem(item);

            // emit ITEM_SELECTED of type @see ToggleGroupItemSelectedEventSata
            toggleGroup.eventEmitter.emit(ToggleGroupEvents.ITEM_SELECTED, {itemIndex: itemIndex});
        }
    }

    public destroy(): void
    {
        // AppDebug.log("ToggleGroup::destroy", AppDebugLogType.DESTROY);
        
        // @ts-ignore
        this.items.forEach((item:Phaser.GameObjects.GameObject)=>{
            item.off(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.OnItemPointerUp);
        });

        this.eventEmitter.destroy();
        this.eventEmitter = null;

        this.items = null;
        this.selectedItem = null;
    }
}